/**
 * Where to go once a record is saved. The admin configures one target per
 * direction on the form (nothing / a screen / a form) and the API sends it as
 * {type, uid} — this turns that into the concrete client action.
 */
export interface RedirectConfig {
    type?: 'screen' | 'form' | null;
    uid?: string | null;
}

export type AfterSaveAction =
    | { kind: 'none' }
    | { kind: 'stay' }
    | { kind: 'navigate'; path: string };

interface ResolveContext {
    app: any;
    appId: string;
    /** id of the saved record — a form target opens over it */
    recordId?: string | number | null;
    /** the form we are on, so pointing at itself means "stay here" */
    currentFormId?: string;
}

/** A form target may be a create or an update form, classic or dynamic. */
const findForm = (app: any, formId: string): any => {
    for (const screen of app?.screens ?? []) {
        const found = [...(screen.createForms ?? []), ...(screen.updateForms ?? [])]
            .find((f: any) => String(f.id) === String(formId));
        if (found) {
            return found;
        }
    }
    return null;
};

export const resolveAfterSave = (
    redirect: RedirectConfig | null | undefined,
    { app, appId, recordId, currentFormId }: ResolveContext
): AfterSaveAction => {
    const type = redirect?.type ?? null;
    const uid = redirect?.uid ?? null;

    if (!type || !uid) {
        return { kind: 'none' };
    }

    if (type === 'screen') {
        return { kind: 'navigate', path: `/app/${appId}/screen/${uid}` };
    }

    if (currentFormId && String(uid) === String(currentFormId)) {
        return { kind: 'stay' };
    }

    const target = findForm(app, String(uid));
    if (!target || recordId === null || recordId === undefined || recordId === '') {
        // Target lives outside this application (or there is no record to open
        // it over) — the form page would render "Form not found", so rather
        // fall back to the default navigation than strand the user.
        return { kind: 'none' };
    }

    const hasOneToMany = target.fields?.some((field: any) => field.type === 'one-to-many');
    const route = hasOneToMany ? 'update-form' : 'update-simple-form';

    return { kind: 'navigate', path: `/app/${appId}/${route}/${uid}/${recordId}` };
};
